"use client";

import type { Editor } from "@tiptap/react";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignJustify,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCallback, useRef, useState } from "react";

interface TiptapToolbarProps {
  editor: Editor | null;
}

// Couleurs prédéfinies pour le texte
const TEXT_COLORS = [
  { label: "Noir", value: "#000000" },
  { label: "Bleu foncé", value: "#0000CC" },
  { label: "Vert corrosion", value: "#22c55e" },
  { label: "Orange corrosion", value: "#f59e0b" },
  { label: "Rouge corrosion", value: "#ef4444" },
  { label: "Bordeaux corrosion", value: "#991b1b" },
  { label: "Gris", value: "#6b7280" },
];

function ToolbarButton({
  onClick,
  isActive = false,
  title,
  children,
}: {
  onClick: () => void;
  isActive?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        "w-9 h-9 flex items-center justify-center rounded-md text-sm transition-colors",
        "hover:bg-muted",
        isActive && "bg-primary/10 text-primary ring-1 ring-primary/20"
      )}
    >
      {children}
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-6 bg-border mx-1" />;
}

export function TiptapToolbar({ editor }: TiptapToolbarProps) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorBtnRef = useRef<HTMLDivElement>(null);

  const setColor = useCallback(
    (color: string) => {
      if (!editor) return;
      if (color === "#000000") {
        editor.chain().focus().unsetColor().run();
      } else {
        editor.chain().focus().setColor(color).run();
      }
      setShowColorPicker(false);
    },
    [editor]
  );

  if (!editor) return null;

  // Couleur active du texte (pour l'indicateur sous le "A")
  const currentColor =
    (editor.getAttributes("textStyle").color as string) ?? "#000000";

  return (
    <div className="flex items-center gap-0.5 flex-wrap px-3 py-2 bg-background border border-border rounded-t-lg border-b-0">
      {/* Undo / Redo */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        title="Annuler"
      >
        <Undo className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        title="Rétablir"
      >
        <Redo className="size-4" />
      </ToolbarButton>

      <ToolbarSeparator />

      {/* Formatage inline */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
        title="Gras"
      >
        <Bold className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
        title="Italique"
      >
        <Italic className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive("underline")}
        title="Souligné"
      >
        <Underline className="size-4" />
      </ToolbarButton>

      <ToolbarSeparator />

      {/* Titres */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive("heading", { level: 1 })}
        title="Titre 1"
      >
        <Heading1 className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive("heading", { level: 2 })}
        title="Titre 2"
      >
        <Heading2 className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive("heading", { level: 3 })}
        title="Titre 3"
      >
        <Heading3 className="size-4" />
      </ToolbarButton>

      <ToolbarSeparator />

      {/* Listes */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
        title="Liste à puces"
      >
        <List className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
        title="Liste numérotée"
      >
        <ListOrdered className="size-4" />
      </ToolbarButton>

      <ToolbarSeparator />

      {/* Alignement */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        isActive={editor.isActive({ textAlign: "left" })}
        title="Aligner à gauche"
      >
        <AlignLeft className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        isActive={editor.isActive({ textAlign: "center" })}
        title="Centrer"
      >
        <AlignCenter className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        isActive={editor.isActive({ textAlign: "justify" })}
        title="Justifier"
      >
        <AlignJustify className="size-4" />
      </ToolbarButton>

      <ToolbarSeparator />

      {/* Couleur du texte */}
      <div className="relative" ref={colorBtnRef}>
        <button
          type="button"
          onClick={() => setShowColorPicker(!showColorPicker)}
          title="Couleur du texte"
          className={cn(
            "w-9 h-9 flex flex-col items-center justify-center rounded-md text-sm transition-colors",
            "hover:bg-muted",
            showColorPicker && "bg-muted"
          )}
        >
          <span className="text-sm font-bold leading-none">A</span>
          <span
            className="w-5 h-1 rounded-full mt-0.5"
            style={{ backgroundColor: currentColor }}
          />
        </button>

        {showColorPicker && (
          <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg p-2 z-50 flex gap-1.5">
            {TEXT_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setColor(c.value)}
                title={c.label}
                className={cn(
                  "w-7 h-7 rounded-md border-2 transition-transform hover:scale-110",
                  currentColor === c.value
                    ? "border-primary ring-2 ring-primary/30"
                    : "border-border"
                )}
                style={{ backgroundColor: c.value }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
